import { Button, Meta } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import PaymentCardDropDown from 'components/park/detail/PaymentCardDropDown';
import VehicleSelectDropDown from 'components/park/detail/VehicleSelectDropDown';
import { Row } from 'components/park/styles/styles';
import { InfoTextSection } from 'components/permit/InfoTextSection';
import { FooterButtonContainer } from 'components/sheets/SheetLayout';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { isWeb } from 'utils/deviceUtils';
import { CenterColumn, ColumnWithMargins } from 'styles/ContainerStyles';
import { PaymentErrorInfoBox } from '../PermitInfoBoxes';
import { SalesOrderReceipt } from '../SalesOrderReceiptClass';

const styles = StyleSheet.create({
  transparentPlaceholder: {
    height: 56,
    opacity: 0,
  },
});

interface SalesOrderPermitEditFooterProps {
  loading: boolean;
  disabled: boolean;
  confirmEditPermit: () => void;
  onVehiclesSelect: () => void;
  hasLicensePlate: boolean;
  hasCard: boolean;
  freeSalesOrder: boolean;
  showLicensePlate?: boolean;
  salesOrderReceipt: SalesOrderReceipt;
  missingRequiredAttachment?: boolean;
  paymentRetrySucceeded?: boolean;
}

const SalesOrderPermitEditFooter: React.FC<SalesOrderPermitEditFooterProps> = ({
  loading,
  disabled,
  confirmEditPermit,
  onVehiclesSelect,
  hasLicensePlate,
  hasCard,
  freeSalesOrder,
  showLicensePlate,
  salesOrderReceipt,
  missingRequiredAttachment = false,
  paymentRetrySucceeded = false,
}) => {
  const { t } = useTranslation();
  const {
    isInvoicing,
    isWaiting,
    isFailedRecurringPaymentForSalesOrder,
    renewal,
  } = salesOrderReceipt;

  const AddCardVehicleInfo = useCallback(() => {
    if (!isWaiting) {
      return null;
    }

    if ((!hasCard && !freeSalesOrder) || !hasLicensePlate) {
      const infoKey =
        !hasCard && hasLicensePlate
          ? 'infoAddPaymentMethod'
          : 'infoAddCardVehicle';

      return <InfoTextSection text={t(`permit.${infoKey}`)} />;
    }

    if (!freeSalesOrder) {
      return <InfoTextSection text={t('permit.paymentInfoText')} />;
    }

    return null;
  }, [hasCard, hasLicensePlate, isWaiting, freeSalesOrder, t]);

  const getButtonText = () => {
    if (isFailedRecurringPaymentForSalesOrder && !paymentRetrySucceeded) {
      return loading ? t('button.processingPayment') : t('button.retryPayment');
    }
    if (missingRequiredAttachment && !isWeb) {
      return t('longTermParking.attachment.addAttachment');
    }
    return t('permit.savePermit');
  };

  return (
    <CenterColumn>
      <AddCardVehicleInfo />
      <ColumnWithMargins ml={20} mr={20} mt={20}>
        {!paymentRetrySucceeded && (
          <PaymentErrorInfoBox
            salesOrderReceipt={salesOrderReceipt}
            loading={loading && !isFailedRecurringPaymentForSalesOrder}
          />
        )}
      </ColumnWithMargins>
      {isFailedRecurringPaymentForSalesOrder &&
        !renewal &&
        !paymentRetrySucceeded && (
          <ColumnWithMargins ml={20} mr={20}>
            <InfoTextSection
              text={t('permit.retryRecurringPaymentActivatesRenewal')}
            />
          </ColumnWithMargins>
        )}
      <FooterButtonContainer pt={0} pb={0}>
        {showLicensePlate ? (
          <VehicleSelectDropDown
            showInfoIconIfNotSelected
            skipAutomaticSelection={isWaiting}
            allowMultipleSelection={true}
            handleVehiclesSelect={onVehiclesSelect}
          />
        ) : (
          <View style={styles.transparentPlaceholder} />
        )}
        {isInvoicing ? (
          <Row>
            <ColumnWithMargins mr={10}>
              <Icon
                testID={'invoicing-icon'}
                name="Document-Outlined"
                size="l"
              />
            </ColumnWithMargins>
            <Meta>{t('paymentMethods.invoicing')}</Meta>
          </Row>
        ) : (
          <PaymentCardDropDown
            showInfoIconIfNotSelected
            isFreeSalesOrder={freeSalesOrder}
            isFailedRecurringPaymentForSalesOrder={
              isFailedRecurringPaymentForSalesOrder
            }
          />
        )}
      </FooterButtonContainer>

      <ColumnWithMargins ml={20} mr={20} mb={15}>
        <Button
          size="l"
          onPress={confirmEditPermit}
          disabled={disabled || paymentRetrySucceeded}
          loading={loading}
          text={getButtonText()}
          testID="edit-permit-button"
        />
      </ColumnWithMargins>
    </CenterColumn>
  );
};

export default SalesOrderPermitEditFooter;
